import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClientSubscription } from '../../types/subscription';
import { SubscriptionBadge } from './SubscriptionBadge';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getDaysRemaining = (endDate: string): number =>
  Math.ceil((new Date(endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

const isExpiringSoon = (endDate: string): boolean => {
  const days = getDaysRemaining(endDate);
  return days <= 30 && days > 0;
};

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const formatPrice = (paise: number, cycle: string): string => {
  const label = cycle === 'MONTHLY' ? '/mo' : cycle === 'QUARTERLY' ? '/qtr' : '/yr';
  return `₹${(paise / 100).toLocaleString('en-IN')}${label}`;
};

interface Props {
  leadId: string;
}

export const SubscriptionInfo: React.FC<Props> = ({ leadId }) => {
  const { data: subscriptions, isLoading } = useQuery<ClientSubscription[]>({
    queryKey: ['subscription', leadId],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscriptions?leadId=${leadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch subscription');
      const result = await response.json();
      return result.data || [];
    },
  });

  if (isLoading) {
    return (
      <div className="px-4 py-3 text-sm text-gray-500">Loading subscription...</div>
    );
  }

  const active = subscriptions?.find((s) => s.status === 'ACTIVE');

  if (!active) {
    return (
      <div className="px-4 py-3 border rounded-lg bg-gray-50">
        <p className="text-sm text-gray-500">No active subscription</p>
      </div>
    );
  }

  const days = getDaysRemaining(active.endDate);
  const expiringSoon = isExpiringSoon(active.endDate);

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Subscription</span>
        <SubscriptionBadge status={active.status} />
      </div>
      <div className="px-4 py-3 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Plan</span>
          <span className="font-medium text-gray-900">{active.planName}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Price</span>
          <span className="font-medium text-gray-900">{formatPrice(active.planPrice, active.billingCycle)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Start date</span>
          <span className="text-gray-900">{formatDate(active.startDate)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Expiry</span>
          <span className={expiringSoon ? 'text-red-600 font-medium' : 'text-gray-900'}>
            {formatDate(active.endDate)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Days remaining</span>
          <span className={`font-semibold ${days <= 0 ? 'text-red-600' : expiringSoon ? 'text-orange-600' : 'text-green-600'}`}>
            {days <= 0 ? 'Expired' : `${days} days`}
          </span>
        </div>
      </div>
    </div>
  );
};
